// src/utils/xlsx.ts
/**
 * Minimal OOXML (.xlsx) workbook writer — a single sheet, bold header row,
 * shared strings, auto-ish column widths. No dependencies.
 *
 * Produces a genuine .xlsx package, so it opens in Excel, Apple Numbers,
 * Google Sheets and LibreOffice alike. (Its predecessor emitted the older
 * SpreadsheetML 2003 ".xls" XML, which Numbers cannot read.)
 */

import { createZip, type ZipEntry } from "./zip";

export type XlsxCellValue = string | number | boolean | null | undefined;

export type XlsxColumn<T> = {
	header: string;
	value: (row: T) => XlsxCellValue;
};

/** Control characters that are illegal in XML 1.0 documents. */
const ILLEGAL_XML_CHARS = new RegExp("[\\x00-\\x08\\x0B\\x0C\\x0E-\\x1F]", "g");

const escapeXml = (value: string): string =>
	value
		.replace(ILLEGAL_XML_CHARS, "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

const toText = (value: XlsxCellValue): string => {
	if (value === null || value === undefined) return "";
	if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
	return String(value);
};

/** 0 → "A", 25 → "Z", 26 → "AA". */
function columnName(index: number): string {
	let name = "";
	let n = index;
	while (n >= 0) {
		name = String.fromCharCode(65 + (n % 26)) + name;
		n = Math.floor(n / 26) - 1;
	}
	return name;
}

/** Excel forbids these characters in sheet names and caps the name at 31 chars. */
const sanitizeSheetName = (name: string): string =>
	name.replace(/[\\/?*[\]:]/g, " ").trim().slice(0, 31) || "Sheet1";

const XML_DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const NS_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const NS_PKG_REL = "http://schemas.openxmlformats.org/package/2006/relationships";
const NS_CT = "http://schemas.openxmlformats.org/package/2006/content-types";
const CT_BASE = "application/vnd.openxmlformats-officedocument.spreadsheetml";

function buildSheetXml<T>(
	rows: readonly T[],
	columns: readonly XlsxColumn<T>[],
	strings: Map<string, number>,
): string {
	// Style 1 is the bold header defined in styles.xml below.
	const cell = (col: number, rowNum: number, value: XlsxCellValue, style?: number): string => {
		const ref = `${columnName(col)}${rowNum}`;
		const s = style ? ` s="${style}"` : "";
		if (typeof value === "number" && Number.isFinite(value)) {
			return `<c r="${ref}"${s}><v>${value}</v></c>`;
		}
		const text = toText(value);
		if (text === "") return `<c r="${ref}"${s}/>`;
		let id = strings.get(text);
		if (id === undefined) {
			id = strings.size;
			strings.set(text, id);
		}
		return `<c r="${ref}"${s} t="s"><v>${id}</v></c>`;
	};

	const body = rows
		.map(
			(row, i) =>
				`<row r="${i + 2}">${columns
					.map((c, j) => cell(j, i + 2, c.value(row)))
					.join("")}</row>`,
		)
		.join("");

	// Header last so its shared-string ids stay stable regardless of body content.
	const header = `<row r="1">${columns
		.map((c, j) => cell(j, 1, c.header, 1))
		.join("")}</row>`;

	const widths = columns
		.map((c, j) => {
			const longest = rows.reduce(
				(max, row) => Math.max(max, toText(c.value(row)).length),
				c.header.length,
			);
			const width = Math.min(Math.max(longest + 2, 10), 60);
			return `<col min="${j + 1}" max="${j + 1}" width="${width}" customWidth="1"/>`;
		})
		.join("");

	const lastRef = `${columnName(columns.length - 1)}${rows.length + 1}`;

	return (
		`${XML_DECL}<worksheet xmlns="${NS_MAIN}">` +
		`<dimension ref="A1:${lastRef}"/>` +
		`<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>` +
		`<sheetFormatPr defaultRowHeight="15"/>` +
		`<cols>${widths}</cols>` +
		`<sheetData>${header}${body}</sheetData>` +
		`</worksheet>`
	);
}

function buildSharedStringsXml(strings: Map<string, number>): string {
	const items = [...strings.keys()]
		.map((s) => `<si><t xml:space="preserve">${escapeXml(s)}</t></si>`)
		.join("");
	return (
		`${XML_DECL}<sst xmlns="${NS_MAIN}" count="${strings.size}" ` +
		`uniqueCount="${strings.size}">${items}</sst>`
	);
}

const STYLES_XML =
	`${XML_DECL}<styleSheet xmlns="${NS_MAIN}">` +
	`<fonts count="2">` +
	`<font><sz val="11"/><name val="Calibri"/></font>` +
	`<font><b/><sz val="11"/><name val="Calibri"/></font>` +
	`</fonts>` +
	`<fills count="2">` +
	`<fill><patternFill patternType="none"/></fill>` +
	`<fill><patternFill patternType="gray125"/></fill>` +
	`</fills>` +
	`<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
	`<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
	`<cellXfs count="2">` +
	`<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
	`<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
	`</cellXfs>` +
	`<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
	`</styleSheet>`;

/** Builds the raw bytes of a single-sheet .xlsx package. */
export function buildXlsxBytes<T>(
	rows: readonly T[],
	columns: readonly XlsxColumn<T>[],
	sheetName = "Sheet1",
): Uint8Array {
	const strings = new Map<string, number>();
	const sheetXml = buildSheetXml(rows, columns, strings);

	const entries: ZipEntry[] = [
		{
			name: "[Content_Types].xml",
			content:
				`${XML_DECL}<Types xmlns="${NS_CT}">` +
				`<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
				`<Default Extension="xml" ContentType="application/xml"/>` +
				`<Override PartName="/xl/workbook.xml" ContentType="${CT_BASE}.sheet.main+xml"/>` +
				`<Override PartName="/xl/worksheets/sheet1.xml" ContentType="${CT_BASE}.worksheet+xml"/>` +
				`<Override PartName="/xl/styles.xml" ContentType="${CT_BASE}.styles+xml"/>` +
				`<Override PartName="/xl/sharedStrings.xml" ContentType="${CT_BASE}.sharedStrings+xml"/>` +
				`</Types>`,
		},
		{
			name: "_rels/.rels",
			content:
				`${XML_DECL}<Relationships xmlns="${NS_PKG_REL}">` +
				`<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/>` +
				`</Relationships>`,
		},
		{
			name: "xl/workbook.xml",
			content:
				`${XML_DECL}<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">` +
				`<sheets><sheet name="${escapeXml(sanitizeSheetName(sheetName))}" ` +
				`sheetId="1" r:id="rId1"/></sheets></workbook>`,
		},
		{
			name: "xl/_rels/workbook.xml.rels",
			content:
				`${XML_DECL}<Relationships xmlns="${NS_PKG_REL}">` +
				`<Relationship Id="rId1" Type="${NS_REL}/worksheet" Target="worksheets/sheet1.xml"/>` +
				`<Relationship Id="rId2" Type="${NS_REL}/styles" Target="styles.xml"/>` +
				`<Relationship Id="rId3" Type="${NS_REL}/sharedStrings" Target="sharedStrings.xml"/>` +
				`</Relationships>`,
		},
		{ name: "xl/styles.xml", content: STYLES_XML },
		{ name: "xl/sharedStrings.xml", content: buildSharedStringsXml(strings) },
		{ name: "xl/worksheets/sheet1.xml", content: sheetXml },
	];

	return createZip(entries);
}
